import type { HospitalReportRow } from '../../types';
import type { ExportPayload, ExportEmployeeRow, ExportReportRow } from './exportTypes';
import type { ExportColumnId, ExportGroupBy } from './exportColumns';
import type { TimeRange } from '../timeRange';
import { formatTimeRangeLabel } from '../timeRange';
import { localizedName } from '@/utils/localized';

interface NormalizeHospitalParams {
  title: string;
  hospitals: HospitalReportRow[];
  selectedTypes: Set<string>;
  statuses: Set<string>;
  currencySymbol: string;
  language: string;
  timeRange: TimeRange;
  columns?: ExportColumnId[];
  groupBy?: ExportGroupBy;
  aggregated?: boolean;
}

export function normalizeHospitalExportData(params: NormalizeHospitalParams): ExportPayload {
  const { title, hospitals, selectedTypes, statuses, currencySymbol, language, timeRange, columns, groupBy = 'hospital', aggregated = false } = params;

  const locale = language === 'he' ? 'he-IL' : 'en-US';
  const isRTL = language === 'he';

  const formattedMonth = formatTimeRangeLabel(timeRange, locale);

  // Flatten all reports from selected hospitals with filtering
  const allReportRows: (ExportReportRow & { _groupKey: string })[] = [];

  for (const h of hospitals) {
    let filtered = h.reports;
    if (selectedTypes.size > 0) {
      filtered = filtered.filter((r) => selectedTypes.has(r.reportTypeCode));
    }
    if (statuses.size > 0) {
      filtered = filtered.filter((r) => statuses.has(r.status));
    }

    const hospitalName = localizedName({ name: h.hospitalName, nameEn: h.hospitalNameEn }, language);
    const empRole = (r: typeof filtered[0]) =>
      r.therapistEmploymentRole
        ? localizedName(r.therapistEmploymentRole, language)
        : '';

    for (const r of filtered) {
      const reportTypeName = r.reportTypeObj
        ? localizedName(r.reportTypeObj, language)
        : r.reportTypeCode;

      let _groupKey: string;
      switch (groupBy) {
        case 'employee':
          _groupKey = r.therapistName;
          break;
        case 'reportType':
          _groupKey = reportTypeName;
          break;
        case 'flat':
          _groupKey = '__flat__';
          break;
        default: // 'hospital'
          _groupKey = hospitalName;
      }

      allReportRows.push({
        date: r.date,
        status: r.status,
        reportType: reportTypeName,
        hospital: hospitalName,
        employee: r.therapistName,
        employmentRole: empRole(r),
        quantity: r.quantity,
        ratePerUnit: r.ratePerUnit,
        hospitalBonus: r.hospitalBonusPerUnit,
        amount: r.calculatedAmount,
        notes: r.description ?? '',
        _groupKey,
      });
    }
  }

  // Group rows
  const groupMap = new Map<string, ExportReportRow[]>();
  for (const r of allReportRows) {
    const key = r._groupKey;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(r);
  }

  // Aggregate reports by reportType within each group when requested
  const maybeAggregate = (reports: ExportReportRow[]): ExportReportRow[] => {
    if (!aggregated) return reports;
    const byType = new Map<string, ExportReportRow>();
    for (const r of reports) {
      const existing = byType.get(r.reportType);
      if (existing) {
        existing.quantity += r.quantity;
        existing.amount += r.amount;
      } else {
        byType.set(r.reportType, { ...r, date: '', notes: '' });
      }
    }
    return [...byType.values()];
  };

  const rows: ExportEmployeeRow[] = [...groupMap.entries()].map(([groupName, reports]) => {
    const reportsAmount = reports
      .filter((r) => r.status !== 'REJECTED')
      .reduce((sum, r) => sum + r.amount, 0);

    const displayName = groupName === '__flat__' ? title : groupName;

    return {
      employeeName: displayName,
      employmentRole: '',
      totalReports: reports.filter((r) => r.status !== 'REJECTED').length,
      totalQuantity: reports
        .filter((r) => r.status !== 'REJECTED')
        .reduce((sum, r) => sum + r.quantity, 0),
      reportsAmount,
      allowancesAmount: 0,
      supervisionBonusAmount: 0,
      totalAmount: reportsAmount,
      reports: maybeAggregate(reports),
      allowances: [],
      supervisionBonuses: [],
    };
  });

  const totals = {
    employeeCount: rows.length,
    reportCount: rows.reduce((sum, r) => sum + r.totalReports, 0),
    quantity: rows.reduce((sum, r) => sum + r.totalQuantity, 0),
    totalAmount: rows.reduce((sum, r) => sum + r.totalAmount, 0),
  };

  return {
    title,
    month: formattedMonth,
    generatedAt: new Date().toLocaleString(locale),
    currencySymbol,
    isRTL,
    language,
    filters: {},
    columns,
    groupBy,
    aggregated,
    rows,
    totals,
  };
}
